// Mall auth — Firebase (compat SDK, same project as the POS PWA).
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: firebase.app.App | null = null;
let auth: firebase.auth.Auth | null = null;

function init() {
  if (!app) {
    app = firebase.initializeApp(firebaseConfig);
    auth = firebase.auth();
    auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
  }
  return auth!;
}

export function onAuthChange(cb: (email: string | null) => void) {
  init().onAuthStateChanged((user) => cb(user?.email || null));
}

/** The initialised compat app — shared with the messaging/push helpers. */
export function firebaseApp(): firebase.app.App {
  init();
  return app!;
}

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = init().currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export async function signInWithGoogle(): Promise<string | null> {
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const result = await init().signInWithPopup(provider);
  return result.user?.email || null;
}

// ── Email link (password-less) ─────────────────────────────────────────
// The sign-in link returns to this origin; the app completes the exchange
// on load (see AppRoot._completeEmailLinkIfPresent). No password is ever
// collected or stored.

const EMAIL_FOR_SIGNIN_KEY = 'emailForSignIn';

export async function sendSignInLink(email: string): Promise<void> {
  const actionCodeSettings = {
    url: window.location.origin + '/',
    handleCodeInApp: true,
  };
  await init().sendSignInLinkToEmail(email, actionCodeSettings);
  window.localStorage.setItem(EMAIL_FOR_SIGNIN_KEY, email);
}

export function isSignInWithEmailLink(): boolean {
  return init().isSignInWithEmailLink(window.location.href);
}

/** Completes an email-link sign-in when the page was opened from the link.
 *  Returns the signed-in email, or null when the URL is not an email link. */
export async function completeEmailLinkSignIn(): Promise<string | null> {
  const auth = init();
  if (!auth.isSignInWithEmailLink(window.location.href)) return null;
  let email = window.localStorage.getItem(EMAIL_FOR_SIGNIN_KEY);
  if (!email) {
    // Link opened on a different device/browser — ask to confirm the address.
    email = window.prompt('Please enter your email to complete sign-in:');
  }
  if (!email) return null;
  const result = await auth.signInWithEmailLink(email, window.location.href);
  window.localStorage.removeItem(EMAIL_FOR_SIGNIN_KEY);
  return result.user?.email || email;
}

export async function signOut(): Promise<void> {
  await init().signOut();
}
